import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
	getProtectedImageUriFromString,
	getProtectedImageUriFromStringResized,
} from '../../utils/FileAccessUtils';
import { Button, Image, Popover } from 'antd';
import './ProtectedImageThumbnails.less';
import { downloadImages } from '../../utils/ImageUtils';
import Resizer from 'react-image-file-resizer';
import pLimit from 'p-limit';
import { useBatchedState } from '../../hooks/useBatchedState';
import { heicWorkerManager } from '../../utils/heicWorkerManager';
import {
	getCachedImage,
	setCachedImage,
	getCacheKey,
	cleanupExpiredCache,
} from '../../utils/heicCache';

// Separate concurrency limits: higher for regular images, lower for CPU-intensive HEIC
const regularLimit = pLimit(50);
const heicLimit = pLimit(3); // Lower concurrency for HEIC to avoid CPU overload

// Helper function to detect if a filename/URL is a HEIC file
const isHeicFile = (filename: string): boolean => {
	const lowerFilename = filename.toLowerCase();
	return lowerFilename.endsWith('.heic') || lowerFilename.endsWith('.heif');
};

// Track in-flight conversions to deduplicate concurrent requests for the same image
const inFlightConversions = new Map<string, Promise<Blob>>();

// Optimized HEIC conversion with caching and deduplication
const convertHeicToJpegCached = async (blob: Blob, photoString: string): Promise<Blob> => {
	const cacheKey = getCacheKey(photoString);

	// Check cache first
	const cachedBlob = await getCachedImage(cacheKey);
	if (cachedBlob) {
		return cachedBlob;
	}

	// Check if conversion is already in progress for this image
	const inFlight = inFlightConversions.get(cacheKey);
	if (inFlight) {
		return inFlight;
	}

	// Start conversion and track it
	const conversionPromise = (async () => {
		try {
			const arrayBuffer = await blob.arrayBuffer();
			const jpegBlob = await heicWorkerManager.convert(arrayBuffer);

			// Cache the result for future use
			setCachedImage(cacheKey, jpegBlob).catch(console.error);

			return jpegBlob;
		} finally {
			// Clean up in-flight tracking when done (success or failure)
			inFlightConversions.delete(cacheKey);
		}
	})();

	inFlightConversions.set(cacheKey, conversionPromise);
	return conversionPromise;
};

// Run cache cleanup on module load (non-blocking)
cleanupExpiredCache().catch(console.error);

export const ProtectedImageThumbnails: React.FC<any> = React.forwardRef(
	(
		{
			photoStrings = [],
			role,
			imageWidth,
			imageHeight,
			resize = false,
			style = {},
			showDownloadAll = false,
			imageDownloadName,
			allResizeComplete,
			showNumberOfImages,
			useFullUrls = false,
			...rest
		},
		ref
	) => {
		const hideAfterIndex = showNumberOfImages && showNumberOfImages - 1;
		const photoUriMapRef = useRef<{ [key: string]: any }>({});
		const [photoUriMap, queueUpdatePhotoUriMap] = useBatchedState<{ [key: string]: any }>({}, 50);
		const hasCalledComplete = useRef(false);

		const photoStringsKey = useMemo(() => photoStrings.join(','), [photoStrings]);

		//If either photostring or resize variable changes, entire component will rerender
		useEffect(() => {
			hasCalledComplete.current = false;
		}, [photoStringsKey, resize]);

		// Cleanup effect: revoke blob URLs and clear ref when photoStringsKey changes
		// This ensures the processing effect re-processes all images from scratch
		// Revoke blob URLs to prevent memory leaks when photoStrings change or on unmount

		useEffect(() => {
			return () => {
				Object.values(photoUriMapRef.current).forEach((uri) => {
					if (typeof uri === 'string' && uri.startsWith('blob:')) {
						URL.revokeObjectURL(uri);
					}
				});
				photoUriMapRef.current = {};
			};
		}, [photoStringsKey]);

		const queueUpdate = (photoString: string, uri: any) => {
			photoUriMapRef.current[photoString] = uri;
			queueUpdatePhotoUriMap({ [photoString]: uri });
		};

		useEffect(() => {
			if (!resize || !allResizeComplete) return;

			let interval: NodeJS.Timeout | null = null;
			let maxTimeout: NodeJS.Timeout | null = null;
			const MAX_WAIT_TIME_MS = 30000; // 30 second maximum wait time

			const completeAndCleanup = () => {
				if (!hasCalledComplete.current) {
					hasCalledComplete.current = true;
					allResizeComplete();
				}
				if (interval) clearInterval(interval);
				if (maxTimeout) clearTimeout(maxTimeout);
			};

			const checkIfComplete = () => {
				const total = photoStrings.length;
				const keys = Object.keys(photoUriMapRef.current);
				const complete =
					total === keys.length &&
					photoStrings.every((key) => {
						const value = photoUriMapRef.current[key];
						return value && value !== 'pending';
					});

				if (complete) {
					completeAndCleanup();
				}
			};

			interval = setInterval(checkIfComplete, 100);
			// Force completion after max wait time to prevent infinite waiting
			maxTimeout = setTimeout(completeAndCleanup, MAX_WAIT_TIME_MS);

			return () => {
				if (interval) clearInterval(interval);
				if (maxTimeout) clearTimeout(maxTimeout);
			};
		}, [resize, photoStrings, allResizeComplete, photoStringsKey]);

		const resizeImageFromUrl = useCallback(
			async (photoString) => {
				const s3url = useFullUrls ? photoString : getProtectedImageUriFromString(role)(photoString);
				const IMAGE_TIMEOUT_MS = 10000; // 10 second timeout per image
				const HEIC_TIMEOUT_MS = 30000; // Longer timeout for HEIC conversion

				try {
					// Add timeout to fetch
					const controller = new AbortController();
					const timeoutId = setTimeout(() => controller.abort(), IMAGE_TIMEOUT_MS);
					const response = await fetch(s3url, { signal: controller.signal });
					clearTimeout(timeoutId);
					let blob = await response.blob();

					// Convert HEIC to JPEG before resizing (with caching and Web Worker)
					if (isHeicFile(photoString)) {
						blob = await convertHeicToJpegCached(blob, photoString);
					}

					const file = new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' });

					// Wrap Resizer in a promise with timeout
					await new Promise<void>((resolve) => {
						const resizeTimeout = setTimeout(
							() => {
								// Timeout - use fallback URL
								queueUpdate(
									photoString,
									useFullUrls
										? photoString
										: getProtectedImageUriFromStringResized(role)(photoString, 600, 800)
								);
								resolve();
							},
							isHeicFile(photoString) ? HEIC_TIMEOUT_MS : IMAGE_TIMEOUT_MS
						);

						Resizer.imageFileResizer(
							file,
							600, // maxWidth
							800, // maxHeight
							'JPEG', // compressFormat
							80, // quality
							0, // rotation
							(uri) => {
								clearTimeout(resizeTimeout);
								queueUpdate(photoString, uri);
								resolve();
							},
							'base64'
						);
					});
				} catch (err) {
					queueUpdate(
						photoString,
						useFullUrls
							? photoString
							: getProtectedImageUriFromStringResized(role)(photoString, 600, 800)
					);
				}
			},
			[role, useFullUrls]
		);

		// Function to process images without resizing, but handling HEIC conversion
		const processImageWithoutResize = useCallback(
			async (photoString: string) => {
				const s3url = useFullUrls ? photoString : getProtectedImageUriFromString(role)(photoString);

				// If not a HEIC file, just use the URL directly
				if (!isHeicFile(photoString)) {
					queueUpdate(photoString, s3url);
					return;
				}

				// For HEIC files, fetch and convert to JPEG (with caching and Web Worker)
				try {
					const response = await fetch(s3url);
					const blob = await response.blob();
					const jpegBlob = await convertHeicToJpegCached(blob, photoString);
					const jpegUrl = URL.createObjectURL(jpegBlob);
					queueUpdate(photoString, jpegUrl);
				} catch (err) {
					// Fallback to original URL if conversion fails
					queueUpdate(photoString, s3url);
				}
			},
			[role, useFullUrls]
		);

		useEffect(() => {
			if (resize) {
				photoStrings.forEach((photoString) => {
					// Use 'pending' as a clear and non-falsy sentinel.
					if (!(photoString in photoUriMapRef.current)) {
						photoUriMapRef.current[photoString] = 'pending';
						// Use appropriate concurrency limit based on file type
						const limiter = isHeicFile(photoString) ? heicLimit : regularLimit;
						limiter(() => resizeImageFromUrl(photoString));
					}
				});
			} else {
				// Process each image, handling HEIC conversion asynchronously
				photoStrings.forEach((photoString) => {
					if (!(photoString in photoUriMapRef.current)) {
						photoUriMapRef.current[photoString] = 'pending';
						// Use appropriate concurrency limit based on file type
						const limiter = isHeicFile(photoString) ? heicLimit : regularLimit;
						limiter(() => processImageWithoutResize(photoString));
					}
				});
			}
		}, [photoStrings, resize, resizeImageFromUrl, processImageWithoutResize, role]);

		return (
			<div
				className="ProtectedImageThumbnails"
				style={{ display: 'flex', flexWrap: 'wrap', ...style }}
				{...rest}
			>
				<Image.PreviewGroup>
					{photoStrings.map((photoString, idx) => {
						const isLastVisible = idx === hideAfterIndex;
						const shouldHide = hideAfterIndex && idx > hideAfterIndex;

						const image = (
							<div
								key={photoString}
								style={{
									position: 'relative',
									marginRight: 8,
									marginBottom: 8,
									width: imageWidth,
									height: imageHeight,
									overflow: 'hidden',
									display: shouldHide ? 'none' : 'block',
								}}
							>
								<Image
									src={photoUriMap[photoString]}
									style={{
										width: imageWidth,
										height: imageHeight,
										objectFit: 'cover',
									}}
								/>
								{isLastVisible && photoStrings.length > hideAfterIndex + 1 && (
									<div
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											backgroundColor: 'rgba(0, 0, 0, 0.5)',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											color: '#fff',
											fontSize: 18,
											fontWeight: 'bold',
											pointerEvents: 'none',
										}}
									>
										+{photoStrings.length - (hideAfterIndex + 1)}
									</div>
								)}
							</div>
						);

						return image;
					})}
				</Image.PreviewGroup>
				{showDownloadAll ? (
					<Popover content={'Download Images'}>
						<Button
							type={'link'}
							onClick={() => downloadImages(photoStrings, role, imageDownloadName)}
						>
							<i style={{ fontSize: imageHeight / 2 }} className={`icons8-font icons8-download`} />
						</Button>
					</Popover>
				) : null}
			</div>
		);
	}
);
